import Button from '@axa-japan/design-system-react/Button';
import Lightbox from '@axa-japan/design-system-react/Lightbox';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import { useState } from 'react';
import imageIconUrl from '../../assets//image_icon.svg';
import trashIconUrl from '../../assets//trash_icon.svg';
import wrongFileUrl from '../../assets//wrong_file.svg';
import fileIconUrl from '../../assets/file_icon.svg';
import imgPlaceholderUrl from '../../assets/img_placeholder.svg';
import { ALLOWED_FILE_EXTENSIONS, FILE_DEFAULT_NAME, MAX_FILE_SIZE, VIEWABLE_FILES } from '../../utils/fileUtils';
import Camera from './Camera';

const isFileViewable = (file: File) =>
  new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = String(e.target?.result);
    };
    fileReader.readAsDataURL(file);
  });

const FileDropzone = ({ onFileChange, fileId }: { onFileChange: (file?: File, src?: string) => void; fileId: string }) => {
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      if (!ALLOWED_FILE_EXTENSIONS.includes(e.target.files[0].type)) {
        setWarningMessage(
          'カメラ撮影・ファイル選択いただいたファイル形式は取扱対象外となります。お手数ですがファイル形式をご確認のうえ、再度、カメラ撮影もしくはファイル選択してください。',
        );
      } else if (e.target.files[0].size > MAX_FILE_SIZE) {
        setWarningMessage('書類のサイズが20MBを超えているため、お取り扱いできません。');
      } else if (VIEWABLE_FILES.includes(e.target.files[0].type) && !(await isFileViewable(e.target.files[0]))) {
        setWarningMessage('撮影またはファイル選択いただいた画像を表示できませんでした。もう一度やり直してください。');
      } else {
        setWarningMessage('');
        onFileChange(e.target.files[0]);
      }
    }
  };

  return (
    <div className="bg-white px-10 pb-5 pt-10 flex flex-col items-center gap-2 rounded-sm border-dashed border-black border-2 border-opacity-30">
      {warningMessage ? (
        <div className="w-3/5 h-full flex flex-col gap-5 pb-5 items-center">
          <img src={wrongFileUrl} />
          <Text color="danger" className="!text-center">
            {warningMessage}
          </Text>
        </div>
      ) : (
        <div className="flex justify-center bg-interactiveHoverGreyTransparent w-3/5 lg:w-2/5 xl:w-1/5 h-52 mb-5">
          <img src={imgPlaceholderUrl} />
        </div>
      )}
      <div className="w-2/3 flex flex-col lg:flex-row justify-between lg:gap-5 gap-2 mb-5">
        <input
          id={`file-uploader-${fileId}`}
          type="file"
          hidden
          onChange={handleFileChange}
          accept=".jpg,.jpeg,.png,.tiff,.pdf,.tif"
          className="bg-blue"
        />
        {isCameraModalOpen && (
          <div className="fixed !z-10 top-0 left-0 w-full h-screen bg-black">
            <Camera onCapture={(file, src) => onFileChange(file, src as string)} onClose={() => setIsCameraModalOpen(false)}></Camera>
          </div>
        )}
        <Button onClick={() => setIsCameraModalOpen(true)} variant="outlined" className="!w-full">
          書類を撮影
        </Button>
        <Button variant="outlined" asChild className="!w-full">
          <label htmlFor={`file-uploader-${fileId}`}>ファイルを選択</label>
        </Button>
      </div>
    </div>
  );
};

const FilePreview = ({ file, onDelete }: { file: File; onDelete: (src: File) => void }) => {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fileSrc = URL.createObjectURL(file);

  return (
    <div className="flex lg:flex-row md:flex-col w-full justify-between gap-5">
      <div className="bg-white flex flex-col items-center justify-between w-full px-5  pt-10 pb-5 gap-5 rounded-sm border-dashed border-black border-2 border-opacity-30">
        {file.type === 'application/pdf' && (
          // TMP pdf logo display in case of PDF file uploaded
          <div className="flex flex-col items-center ">
            <div>
              <img src={fileIconUrl} alt="PDF Icon" className="min-h-28 mx-auto" />
            </div>
            <div>
              <Text className="line-clamp-1">{file.name}</Text>
            </div>
          </div>
        )}

        {file.type === 'image/tiff' && (
          <div className="flex flex-col items-center ">
            <div>
              <img src={imageIconUrl} alt="TIFF Icon" className="min-h-28 mx-auto" />
            </div>
            <div>
              <Text className="line-clamp-1">{file.name}</Text>
            </div>
          </div>
        )}

        {file.type !== 'image/tiff' && file.type !== 'application/pdf' && (
          <div className="bg-interactiveHoverGrey w-2/5 h-full flex">
            <img src={fileSrc} alt="Preview" className="img-preview" onClick={() => setIsPreviewModalOpen(true)} />
            <Lightbox
              open={isPreviewModalOpen}
              onClose={() => setIsPreviewModalOpen(false)}
              slides={[
                {
                  src: fileSrc,
                },
              ]}
            />
          </div>
        )}

        <div className="flex flex-col items-center">
          <StatusModal
            id="delete-file-modal"
            title="画像を削除します"
            open={isDeleteModalOpen}
            setOpen={setIsDeleteModalOpen}
            content="撮影・ファイル選択したファイルは削除されますがよろしいでしょうか。"
            buttons={[
              {
                text: 'キャンセル',
              },
              {
                text: 'OK',
                onClick: () => onDelete(file),
              },
            ]}
          />
          <Button variant="text" icon={<img src={trashIconUrl} />} onClick={() => setIsDeleteModalOpen(true)} iconPosition="left">
            削除
          </Button>
        </div>
      </div>
    </div>
  );
};

type FileUploadProps = {
  file?: File;
  onFileChange: (file?: File, src?: string) => void;
  fileId: string;
  receptionNo: string;
  index: number;
  formId: string;
};

const FileUpload = ({ onFileChange, file, fileId, receptionNo, index, formId }: FileUploadProps) => {
  const handleFileChange = (file?: File) => {
    if (!file) {
      onFileChange(undefined);
      return;
    }
    const seq = String(index + 1).padStart(2, '0');
    const dotIndex = file.name.lastIndexOf('.');
    const ext = dotIndex !== -1 ? file.name.slice(dotIndex + 1) : '';
    const newFileName = ext ? `${receptionNo}_${formId}_${seq}.${ext}` : `${receptionNo}_${formId}_${seq}`;
    const newFile = new File([file], newFileName, { type: file.type, lastModified: file.lastModified });
    onFileChange(newFile);
  };

  const handleFileRemove = () => {
    const file = new File([], FILE_DEFAULT_NAME, { type: 'JPEG' });
    onFileChange(file);
  };

  return file && file?.name !== FILE_DEFAULT_NAME ? (
    <FilePreview file={file} onDelete={handleFileRemove} />
  ) : (
    <FileDropzone fileId={fileId} onFileChange={(file) => handleFileChange(file)} />
  );
};

export default FileUpload;
